import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { grantConsent, revokeConsent, trackPageView } from "../utils/analytics";

const STORAGE_KEY = "cookie_consent";

type Consent = "accepted" | "declined" | null;

function getConsent(): Consent {
  const value = localStorage.getItem(STORAGE_KEY);
  if (value === "accepted" || value === "declined") return value;
  return null;
}

export default function CookieConsent() {
  const { t } = useTranslation("common");
  const location = useLocation();
  const [consent, setConsent] = useState<Consent>(getConsent);

  useEffect(() => {
    if (consent === "accepted") {
      trackPageView(location.pathname);
    }
  }, [consent, location.pathname]);

  function accept() {
    localStorage.setItem(STORAGE_KEY, "accepted");
    grantConsent();
    setConsent("accepted");
  }

  function decline() {
    localStorage.setItem(STORAGE_KEY, "declined");
    revokeConsent();
    setConsent("declined");
  }

  if (consent !== null) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-4">
      <div
        className="mx-auto flex max-w-4xl flex-col items-center gap-4 rounded-xl
                    border border-slate-200 bg-white/95 px-6 py-4 shadow-lg
                    backdrop-blur sm:flex-row sm:justify-between"
      >
        <p className="text-sm text-slate-600">
          {t("cookieConsent.message")}{" "}
          <Link to="/cookies" className="text-indigo-600 hover:underline">
            {t("cookieConsent.learnMore")}
          </Link>
        </p>

        <div className="flex shrink-0 gap-3">
          <button
            onClick={decline}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm
                       font-medium text-slate-700 transition-colors hover:bg-slate-50
                       active:bg-slate-100"
          >
            {t("cookieConsent.decline")}
          </button>
          <button
            onClick={accept}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white
                       transition-colors hover:bg-indigo-700 active:bg-indigo-800"
          >
            {t("cookieConsent.accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
